import { Button } from "@/components/flexkit/Button";
import { DialogHeader, DialogTitle } from "@/components/podkit/dialog/Dialog";
import { toast } from "@/components/podkit/toasts/use-toast";
import { ModelSelect, ProviderFields } from "@/components/runners/details/LLMIntegrationComponents";
import { Provider, type LLMProviderKind } from "@/components/runners/details/types";
import { buildEndpointUrl, ANTHROPIC_ENDPOINT } from "@/components/runners/details/llm-integration-utils";
import type { TrackLocation } from "@/hooks/use-segment";
import { useCreateLLMIntegration } from "@/queries/runner-configuration-queries";
import { formatError } from "@/utils/errors";
import { SupportedModel } from "gitpod-next-api/gitpod/v1/agent_pb";
import { useState, type FC } from "react";

type LLMIntegrationAddModalProps = {
    runnerId: string;
    provider?: LLMProviderKind;
    onClose: () => void;
    "data-track-location": TrackLocation;
};
export const LLMIntegrationAddModal: FC<LLMIntegrationAddModalProps> = ({
    runnerId,
    provider,
    onClose,
    "data-track-location": dataTrackLocation,
}) => {
    const [endpoint, setEndpoint] = useState(provider === Provider.Anthropic ? ANTHROPIC_ENDPOINT : "");
    const [bedrockModelId, setBedrockModelId] = useState("");
    const [apiKey, setApiKey] = useState("");
    const [vertexRegion, setVertexRegion] = useState("");
    const [vertexModelVersion, setVertexModelVersion] = useState("");
    const [selectedModel, setSelectedModel] = useState<SupportedModel>(SupportedModel.SONNET_4_5);
    const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);

    const handleFileUpload = (content: string, fileName: string) => {
        setApiKey(content);
        setUploadedFileName(fileName);
    };

    const handleFileRemove = () => {
        setUploadedFileName(null);
        setApiKey("");
    };

    const createIntegration = useCreateLLMIntegration(runnerId);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate required fields per provider
        if (provider === Provider.Bedrock && bedrockModelId.trim() === "") {
            toast({
                title: "Model ID required",
                description: "Enter a Bedrock model ID (e.g., eu.anthropic.claude-sonnet-4-20250514-v1:0)",
            });
            return;
        }

        const trimmedEndpoint = buildEndpointUrl(
            provider || Provider.Unknown,
            bedrockModelId,
            vertexRegion,
            vertexModelVersion,
            endpoint,
        ).trim();
        if (!trimmedEndpoint) {
            toast({
                title: "Endpoint required",
                description: "Please enter an LLM endpoint URL",
            });
            return;
        }

        // Provider-specific validation and normalization
        if (provider === Provider.Vertex) {
            if (!vertexRegion.trim() || !vertexModelVersion.trim()) {
                toast({
                    title: "Missing fields",
                    description: "Please provide both region and model version",
                });
                return;
            }
        } else if (provider === Provider.Anthropic) {
            // Endpoint is fixed for Anthropic
        }

        // Bedrock uses AWS credentials, no API key validation needed
        if (provider === Provider.Bedrock) {
            // Skip API key validation for Bedrock
        } else if (!apiKey.trim()) {
            if (provider === Provider.Vertex) {
                toast({
                    title: "Service account required",
                    description: "Please upload a Google Cloud service account JSON file",
                });
            } else {
                toast({
                    title: "API key required",
                    description: "Please enter your LLM provider API key",
                });
            }
            return;
        }

        try {
            await createIntegration.mutateAsync({
                models: [selectedModel],
                endpoint: trimmedEndpoint,
                apiKey: provider === Provider.Bedrock ? "ignore" : apiKey.trim(),
                maxTokens: 0, // Use model default
            });

            toast({
                title: "LLM integration created",
                description: "Your LLM integration has been successfully configured",
            });

            onClose();
        } catch (error) {
            toast({
                title: "Failed to create LLM integration",
                description: formatError(error),
            });
        }
    };

    return (
        <div className="flex flex-col gap-6" data-track-location={dataTrackLocation}>
            <DialogHeader>
                <DialogTitle>Add LLM Integration</DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <ProviderFields
                    provider={provider || Provider.Unknown}
                    bedrockModelId={bedrockModelId}
                    onBedrockModelIdChange={setBedrockModelId}
                    vertexRegion={vertexRegion}
                    onVertexRegionChange={setVertexRegion}
                    vertexModelVersion={vertexModelVersion}
                    onVertexModelVersionChange={setVertexModelVersion}
                    endpoint={endpoint}
                    onEndpointChange={setEndpoint}
                    uploadedFileName={uploadedFileName}
                    onFileUpload={handleFileUpload}
                    onFileRemove={handleFileRemove}
                    apiKey={apiKey}
                    onApiKeyChange={setApiKey}
                    isEdit={false}
                />

                <ModelSelect selectedModel={selectedModel} onModelChange={setSelectedModel} />

                <div className="flex justify-end gap-2 pt-4">
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={onClose}
                        data-tracking-id="cancel-add-llm-integration-llm-integration-add-modal"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="primary"
                        loading={createIntegration.isPending}
                        data-testid="create-llm-integration-button"
                        data-tracking-id="confirm-add-llm-integration-llm-integration-add-modal"
                    >
                        Create Integration
                    </Button>
                </div>
            </form>
        </div>
    );
};
